import React from "react";
import { Card, CardBody, CardHeader, Table, Button, Dropdown } from "react-bootstrap";
import FormCheck from 'react-bootstrap/FormCheck';
import { getWeekStartForDate } from '../../utils/scheduleGenerators/dateHelpers';
import { buildBlockRotationRanges, weekendFallsWithinBlockRange } from '../../utils/weekendSchedule/weekendScheduleGenerator';

// This component renders the Weekend Call tab UI using values/handlers provided by the parent.
const WeekendCallTab = ({
  schedules,
  currentMonthIndex,
  handleMonthNavigation,
  getAllMonths,
  getWeekendsForMonth,
  isEditMode,
  onEditClick,
  onSaveChanges,
  onCancel,
  onExportCalendar,
  onExportExcel,
  onExportPrint,
  allowRotationMovement,
  setAllowRotationMovement,
  editedWeekendSchedules,
  originalWeekendSchedules,
  movedResidents,
  draggedResident,
  handleDragStart,
  handleDragOver,
  handleDrop,
  rotationColors,
  capitalizeName,
  abbreviateRotation,
  onClearWeekendDayForRotation,
  onClearMonthForRotation,
  extraUnassignedByRotationMonth,
  approvedVacationRequests = [],
  getRotationColor,
  getWeekStartForDate: getWeekStartForDateProp,
}) => {
  // Use prop if provided, otherwise use imported function
  const getWeekStart = getWeekStartForDateProp || getWeekStartForDate;
  if (Object.keys(schedules).length === 0 || getAllMonths(schedules).length === 0) {
    return (
      <Card className="mb-4 shadow-sm border-0 py-4">
        <CardBody className="p-0">
          <div className="text-center py-4">
            <div className="d-flex flex-column align-items-center gap-2">
              <img src="/images/undraw_schedule_6t8k.svg" alt="No schedule" className="img-fluid" style={{height: "200px"}} />
              <h5>{Object.keys(schedules).length === 0 ? 'No weekend call schedule' : 'No months available in schedule'}</h5>
              <p className="text-muted">Please create a rotation schedule first</p>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  const currentMonth = getAllMonths(schedules)[currentMonthIndex];
  const weekends = currentMonth ? getWeekendsForMonth(currentMonth) : [];

  const monthStr = currentMonth;

  const getResidentsOnRotationForMonth = (rotation) => {
    const residentsOnRotation = [];
    const residentIdsAdded = new Set();
    
    // Build block rotation ranges for all schedules
    const blockRotationRanges = buildBlockRotationRanges(schedules);
    
    Object.values(schedules).forEach((scheduleData) => {
      if (!scheduleData || !scheduleData.schedule) return;
      const isBlocks = scheduleData.scheduleType === 'blocks';
      const isWeekly = scheduleData.scheduleType === 'weekly';
      
      Object.entries(scheduleData.schedule).forEach(([residentId, residentData]) => {
        if (!residentData || !residentData.rotations) return;
        if (residentIdsAdded.has(residentId)) return; // Avoid duplicates
        
        let isOnRotation = false;
        
        if (isBlocks) {
          // For block schedules, check if resident is on this rotation during any weekend in the month
          const ranges = blockRotationRanges?.[residentId]?.[rotation];
          if (ranges && ranges.length > 0) {
            // Check if any weekend in the month falls within the rotation ranges
            isOnRotation = weekends.some(weekend => {
              return weekendFallsWithinBlockRange(ranges, weekend);
            });
          }
        } else if (isWeekly) {
          // For weekly schedules, check if resident is on this rotation during any weekend in the month
          isOnRotation = weekends.some(weekend => {
            const weekKey = getWeekStart(new Date(weekend.saturday + 'T00:00:00'));
            return residentData.rotations[weekKey] === rotation;
          });
        } else {
          // For monthly schedules, check the month string
          const rot = residentData.rotations[monthStr];
          isOnRotation = rot === rotation;
        }
        
        if (isOnRotation) {
          residentsOnRotation.push({ id: residentId, name: residentData.name, year: residentData.year });
          residentIdsAdded.add(residentId);
        }
      });
    });
    return residentsOnRotation;
  };

  const getAssignedResidentIdsForRotationMonth = (rotation) => {
    const ids = new Set();
    (weekends || []).forEach((wk) => {
      [wk.saturday, wk.sunday].forEach((d) => {
        const list = (editedWeekendSchedules[rotation]?.[d]?.residents) || [];
        list.forEach((r) => ids.add(r.id));
      });
    });
    return ids;
  };

  const getResidentAssignmentCountForMonth = (rotation, residentId) => {
    let count = 0;
    (weekends || []).forEach((wk) => {
      [wk.saturday, wk.sunday].forEach((d) => {
        const list = (editedWeekendSchedules[rotation]?.[d]?.residents) || [];
        if (list.some((r) => r.id === residentId)) count += 1;
      });
    });
    return count;
  };

  return (
    <>
      {/* Vacation Card - Shows residents who are on vacation during the current month */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Header className="fw-semibold d-flex border-0 justify-content-between align-items-center px-4 pt-4">
          <h6 className="fw-semibold mb-0">
            <span className="badge bg-secondary rounded-2 me-2"><i className="bi bi-calendar-check"></i></span>
            Leave for the Month of {" "}
            {(() => {
              const months = getAllMonths(schedules);
              const currentMonthStr = months && months[currentMonthIndex];
              if (!currentMonthStr) return null;
              const [displayYear, displayMonth] = currentMonthStr.split('-').map(str => parseInt(str, 10));
              return new Date(displayYear, displayMonth - 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
            })()}
          </h6>
        </Card.Header>
        <Card.Body>
          {(() => {
            // Figure out the current month being displayed for weekend-call tab
            const months = getAllMonths(schedules);
            const currentMonthStr = months && months[currentMonthIndex];
            if (!currentMonthStr) {
              return <div className="text-muted">No schedule available.</div>;
            }
            const [displayYear, displayMonth] = currentMonthStr.split('-').map(str => parseInt(str, 10));

            // Find residents with approved leave requests for the current month
            const residentsOnVacation = [];
            const monthStart = new Date(displayYear, displayMonth - 1, 1);
            const monthEnd = new Date(displayYear, displayMonth, 0, 23, 59, 59);

            // Filter approved vacation requests that overlap with the current month
            const currentMonthRequests = approvedVacationRequests.filter(request => {
              const requestStart = new Date(request.startDate);
              const requestEnd = new Date(request.endDate);
              
              // Check if the request overlaps with the current month
              return requestStart <= monthEnd && requestEnd >= monthStart;
            });

            // Group requests by resident and format the data
            const residentsMap = new Map();
            currentMonthRequests.forEach(request => {
              const residentName = request.resident ? 
                `${request.resident.firstName} ${request.resident.lastName}` : 
                'Unknown Resident';
              
              if (!residentsMap.has(residentName)) {
                residentsMap.set(residentName, []);
              }
              
              // Format the date range - parse dates as local to avoid timezone issues
              const startDate = new Date(request.startDate + 'T00:00:00');
              const endDate = new Date(request.endDate + 'T00:00:00');
              const startStr = startDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              const endStr = endDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              
              residentsMap.get(residentName).push({
                type: request.type,
                dateRange: `${startStr} - ${endStr}`,
                reason: request.reason || ''
              });
            });

            // Convert map to array format and add rotation info
            residentsMap.forEach((requests, residentName) => {
              const timeframes = requests.map(req => 
                `${req.type} (${req.dateRange})`
              ).join(', ');
              
              // Find the resident's rotation for this month
              let currentRotation = 'Unknown';
              Object.values(schedules).forEach(pgyData => {
                if (pgyData && pgyData.schedule) {
                  const isWeekly = pgyData.scheduleType === 'weekly';
                  const isBlocks = pgyData.scheduleType === 'blocks';
                  Object.entries(pgyData.schedule).forEach(([residentId, residentData]) => {
                    if (residentData.name === residentName && residentData.rotations) {
                      if (isWeekly || isBlocks) {
                        // For weekly/block schedules, find rotation for any week in this month
                        const [year, month] = currentMonthStr.split('-').map(Number);
                        const monthEndDate = new Date(year, month, 0);
                        
                        for (let day = 1; day <= monthEndDate.getDate(); day++) {
                          const checkDate = new Date(year, month - 1, day);
                          const weekKey = getWeekStart(checkDate);
                          const weekRotation = residentData.rotations[weekKey];
                          if (weekRotation) {
                            currentRotation = weekRotation;
                            break;
                          }
                        }
                      } else {
                        // Monthly schedule
                        if (residentData.rotations[currentMonthStr]) {
                          currentRotation = residentData.rotations[currentMonthStr];
                        }
                      }
                    }
                  });
                }
              });
              
              residentsOnVacation.push({
                name: residentName,
                timeframe: timeframes,
                rotation: currentRotation
              });
            });

            if (residentsOnVacation.length === 0) {
              return (
                <div className="text-muted">
                  No approved leave requests this month.
                </div>
              );
            }

            return (
              <div className="row">
                {residentsOnVacation.map((data, idx) => (
                  <div key={idx} className="col-lg-4 col-md-6 col-sm-12 mb-2">
                    <div className="p-2 rounded bg-light">
                      <div className="d-flex flex-row justify-content-between align-items-center">
                        <div className="d-flex flex-row align-items-center gap-2">
                          <span 
                            className="badge rounded-2" 
                            style={{
                              backgroundColor: getRotationColor ? getRotationColor(data.rotation) : (rotationColors[data.rotation] || '#6B7280'),
                              color: 'white',
                              fontSize: '0.75em',
                              fontWeight: '600'
                            }}
                          >
                            {abbreviateRotation(data.rotation)}
                          </span>
                          <strong>{data.name}</strong>
                        </div>
                        <span 
                          className="text-primary fw-semibold" 
                          style={{fontSize: "0.875em"}}
                          dangerouslySetInnerHTML={{
                            __html: data.timeframe.replace(/(Vacation|Sick Leave|Personal Leave|Conference|Other)/g, '<span class="text-muted">$1</span>')
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </Card.Body>
      </Card>

    <Card className="mb-4 shadow-sm border-0">
      <CardHeader className="px-4 py-3 d-flex border-bottom flex-row justify-content-between align-items-start">
        <div className="d-flex flex-column">
          <h6 className="fw-semibold mb-0">Weekend Call Schedule</h6>
          <p className="text-muted small mb-0">{new Date(currentMonth).toLocaleString("default", { month: "long", year: "numeric" })}</p>
        </div>
        <div className="d-flex align-items-center gap-2">
        {/* Controls moved to parent tab header in RotationSchedulerPage */}
    
        {!isEditMode && (
          <div className="d-flex align-items-center gap-2">
            {getAllMonths(schedules).length > 0 && currentMonthIndex >= 0 && (
              <>
                <div className="radio-inputs d-flex align-items-center" style={{ width: "fit-content" }}>
                  <label className="radio">
                    <input
                      type="radio"
                      checked={false}
                      readOnly
                      onClick={() => handleMonthNavigation("prev")}
                      disabled={currentMonthIndex === 0}
                    />
                    <span
                      className="name bg-white-hover px-2"
                      onClick={() => currentMonthIndex !== 0 && handleMonthNavigation("prev")}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </span>
                  </label>

                  <div className="dropdown-container">
                    <Dropdown>
                      <Dropdown.Toggle variant="light-solid" size="sm">
                        {(() => {
                          if (!currentMonth) return "No Month Available";
                          const [year, month] = currentMonth.split("-");
                          return new Date(year, parseInt(month) - 1).toLocaleString("default", { month: "long", year: "numeric" });
                        })()}
                      </Dropdown.Toggle>
                      <Dropdown.Menu style={{ maxHeight: '200px', overflowY: 'auto', width: 'auto' }}>
                        {getAllMonths(schedules).map((m, idx) => (
                          <Dropdown.Item key={m} active={idx === currentMonthIndex} onClick={() => handleMonthNavigation(idx)}>
                            {(() => {
                              const [y, mo] = m.split("-");
                              return new Date(y, parseInt(mo) - 1).toLocaleString("default", { month: "long", year: "numeric" });
                            })()}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <label className="radio">
                    <input
                      type="radio"
                      checked={false}
                      readOnly
                      onClick={() => handleMonthNavigation("next")}
                      disabled={currentMonthIndex === getAllMonths(schedules).length - 1}
                    />
                    <span
                      className="name bg-white-hover px-2"
                      onClick={() =>
                        currentMonthIndex !== getAllMonths(schedules).length - 1 &&
                        handleMonthNavigation("next")
                      }
                    >
                      <i className="bi bi-chevron-right"></i>
                    </span>
                  </label>
                </div>
              </>
            )}
          </div>
        )}
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <Table responsive striped className="custom-border">
          <thead>
          <tr className="custom-header-bg">
                      <th style={{ backgroundColor: "var(--light-bg)" }}></th>
                      {getWeekendsForMonth(
                        getAllMonths(schedules)[currentMonthIndex]
                      ).map((weekend) => (
                        <th
                          key={weekend.saturday}
                          colSpan="2"
                          style={{ backgroundColor: "var(--light-bg)" }}
                        >
                          {(() => {
                            const [year, month, day] = weekend.saturday.split("-").map(Number);
                            const saturdayDate = new Date(year, month - 1, day);
                            return saturdayDate.toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            });
                          })()}
                          -{" "}
                          {(() => {
                            const [year, month, day] = weekend.sunday.split("-").map(Number);
                            const sundayDate = new Date(year, month - 1, day);
                            return sundayDate.toLocaleDateString(undefined, {
                              day: "numeric",
                            });
                          })()}
                        </th>
                      ))}
                      {isEditMode && (
                        <th style={{ backgroundColor: "var(--light-bg)" }}></th>
                      )}
                    </tr>
                    <tr>
                      <th className="ps-4">Rotations</th>
                      {getWeekendsForMonth(
                        getAllMonths(schedules)[currentMonthIndex]
                      ).map((weekend) => (
                        <React.Fragment key={weekend.saturday}>
                          <th>Sat</th>
                          <th>Sun</th>
                        </React.Fragment>
                      ))}
                      {isEditMode && (
                        <th style={{ backgroundColor: "var(--light-bg)" }}>Unassigned</th>
                      )}
                    </tr>
          </thead>
          <tbody>
            {Object.keys(editedWeekendSchedules).map((rotation) => (
              <tr key={`weekend-row-${rotation}`}>
                <td className={`${isEditMode ? 'ps-2' : 'ps-4'}`}>
                    <div className="d-flex flex-row align-items-center">
                  <span className="badge rounded-1" style={{ backgroundColor: rotationColors[rotation] || '#6B7280', color: 'white', fontSize: '0.875rem', fontWeight: '600' }}>
                    {abbreviateRotation ? abbreviateRotation(rotation) : rotation}
                  </span>
                  {isEditMode && (
                    <Button variant="outline-danger" size="sm" className="py-0 px-1 ms-1" title="Remove all weekend call assignments for this month"
                      onClick={() => onClearMonthForRotation && onClearMonthForRotation(rotation)}>
                      <i className="bi bi-x d-inline-block"></i>
                    </Button>
                  )}</div>
                </td>
                {weekends.map((weekend, idx) => (
                  <React.Fragment key={`${rotation}-${idx}`}>
                    <td
                      className="text-center"
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(rotation, weekend.saturday)}
                      style={{ verticalAlign: 'top' }}
                    >
                      {isEditMode && (editedWeekendSchedules[rotation]?.[weekend.saturday]?.residents || []).length > 0 && (
                        <div className="d-flex justify-content-end">
                          <Button variant="outline-danger" size="sm" className="py-0 px-1" title="Remove all from Saturday"
                            onClick={() => onClearWeekendDayForRotation && onClearWeekendDayForRotation(rotation, weekend.saturday)}>
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      )}
                      {(editedWeekendSchedules[rotation]?.[weekend.saturday]?.residents || []).map((resident) => (
                        <div
                          key={`${rotation}-${weekend.saturday}-${resident.id}`}
                          draggable={isEditMode}
                          onDragStart={() => handleDragStart(resident, rotation, weekend.saturday)}
                          style={{
                            cursor: isEditMode ? 'move' : 'default',
                            padding: '4px',
                            margin: '2px 0',
                            backgroundColor: isEditMode ? 'var(--gray-300)' : 'transparent',
                            borderRadius: '4px',
                            opacity: draggedResident?.resident.id === resident.id ? '0.5' : '1',
                            ...(movedResidents.has(`${resident.id}-${rotation}-${weekend.saturday}`) && { backgroundColor: 'var(--green-light)' }),
                          }}
                        >
                          {capitalizeName(resident.name)} (PGY-{resident.year})
                        </div>
                      ))}
                    </td>
                    <td
                      className="text-center"
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(rotation, weekend.sunday)}
                      style={{ verticalAlign: 'top' }}
                    >
                      {isEditMode && (editedWeekendSchedules[rotation]?.[weekend.sunday]?.residents || []).length > 0 && (
                        <div className="d-flex justify-content-end">
                          <Button variant="outline-danger" size="sm" className="py-0 px-1" title="Remove all from Sunday"
                            onClick={() => onClearWeekendDayForRotation && onClearWeekendDayForRotation(rotation, weekend.sunday)}>
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      )}
                      {(editedWeekendSchedules[rotation]?.[weekend.sunday]?.residents || []).map((resident) => (
                        <div
                          key={`${rotation}-${weekend.sunday}-${resident.id}`}
                          draggable={isEditMode}
                          onDragStart={() => handleDragStart(resident, rotation, weekend.sunday)}
                          style={{
                            cursor: isEditMode ? 'move' : 'default',
                            padding: '4px',
                            margin: '2px 0',
                            backgroundColor: isEditMode ? 'var(--gray-300)' : 'transparent',
                            borderRadius: '4px',
                            opacity: draggedResident?.resident.id === resident.id ? '0.5' : '1',
                            ...(movedResidents.has(`${resident.id}-${rotation}-${weekend.sunday}`) && { backgroundColor: 'var(--green-light)' }),
                          }}
                        >
                          {capitalizeName(resident.name)} (PGY-{resident.year})
                        </div>
                      ))}
                    </td>
                  </React.Fragment>
                ))}
                {isEditMode && (
                  <td style={{ verticalAlign: 'top' }}>
                    {(() => {
                      const onRotation = getResidentsOnRotationForMonth(rotation);
                      const visibleItems = [];
                      onRotation.forEach((res) => {
                        const assignedCount = getResidentAssignmentCountForMonth(rotation, res.id);
                        const totalSlots = ((weekends || []).length) + 1; // allow up to weekends + 1
                        const remaining = Math.max(0, totalSlots - assignedCount);
                        if (remaining > 0) {
                          visibleItems.push({ ...res, remaining });
                        }
                      });
                      if (visibleItems.length === 0) return <span className="text-muted small">None</span>;
                      return visibleItems.map((r) => (
                        <div
                          key={`unassigned-${rotation}-${r.id}`}
                          draggable={isEditMode}
                          onDragStart={() => handleDragStart({ id: r.id, name: r.name, year: r.year }, rotation, null)}
                          className="d-flex justify-content-between align-items-center small"
                          style={{ padding: '4px', margin: '2px 0', backgroundColor: 'var(--gray-300)', color: 'var(--gray-800)', borderRadius: '4px', cursor: isEditMode ? 'move' : 'default' }}
                          title={`${r.remaining} available weekend${r.remaining !== 1 ? 's' : ''} to assign`}
                        >
                          <span>{capitalizeName(r.name)}</span>
                          <span style={{ padding: '2px 4px', backgroundColor: 'var(--gray-300)', color: 'var(--gray-800)' }}>x{r.remaining}</span>
                        </div>
                      ));
                    })()}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </CardBody>
    </Card>
    </>
  );
};

export default WeekendCallTab;

